import React, { useEffect, useMemo, useState } from "react";
import { Loader2, FileDown, Mail, Link as LinkIcon, Plus, X, CheckCircle2, XCircle, Search, FileText, Wallet, Trash2, Copy, CalendarClock, ExternalLink, UserRound } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../../lib/ist";
import ModalPortal from "../../components/ModalPortal";

const STATUS_CLS = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  due: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const CURRENCY_SYMBOL = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ",
  SGD: "S$", CAD: "C$", AUD: "A$", JPY: "¥",
};
const SUPPORTED_CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "CAD", "AUD"];

const PAYMENT_METHOD_LABELS = {
  razorpay: "Razorpay", upi: "UPI", netbanking: "Net Banking",
  card: "Card", cash: "Cash", bank_transfer: "Bank Transfer",
  cheque: "Cheque", wallet: "Wallet", paytm: "Paytm", pos: "POS",
  yespay_biz: "YesPay Biz", au_qr_merchant: "AU QR Merchant",
  bijlipay: "Bijlipay", mswipe_pbl: "mSwipe Pay By Link", other: "Other",
};
const PM_LABEL = (m) => PAYMENT_METHOD_LABELS[m] || m;

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [marking, setMarking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/invoices/list?status=${filter}&q=${encodeURIComponent(q)}`);
      setItems(data);
    } catch { toast.error("Failed to load invoices"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filteredCounts = useMemo(() => {
    const c = { all: items.length, paid: 0, due: 0, cancelled: 0 };
    for (const i of items) c[i.status] = (c[i.status] || 0) + 1;
    return c;
  }, [items]);

  const sendInvoice = async (inv) => {
    try {
      await api.post(`/admin/invoices/${inv.id}/send`, { attach_pdf: true });
      toast.success("Invoice emailed to " + inv.customer_email);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const generateLink = async (inv) => {
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/payment-link`);
      navigator.clipboard.writeText(data.payment_link || "");
      toast.success("Payment link generated & copied");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Razorpay link failed"); }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.number}?`)) return;
    try {
      await api.post(`/admin/invoices/${inv.id}/cancel`, {});
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (inv, copy = "admin") => {
    try {
      const r = await api.get(`/admin/invoices/${inv.id}/pdf?copy=${copy}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = copy === "customer" ? `${inv.number}.pdf` : `${inv.number}-admin.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("PDF download failed"); }
  };

  const copyLink = (inv) => {
    if (!inv.payment_link) { toast.error("No Razorpay link yet — generate one first"); return; }
    navigator.clipboard.writeText(inv.payment_link);
    toast.success("Payment link copied");
  };
  const [editingDates, setEditingDates] = useState(null);

  return (
    <div className="space-y-4" data-testid="admin-invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <FileText size={20} className="text-blue-400" /> Invoice Management
          </h2>
          <p className="text-slate-400 text-sm">Create invoices, generate Razorpay links, record manual payments, send emails.</p>
        </div>
        <button onClick={() => setImporting(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="import-csv-btn"><FileText size={14} /> Import CSV</button>
        <button onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2" data-testid="create-invoice-btn"><Plus size={14} /> Create Invoice</button>
      </div>

      {/* Filters + search */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex rounded-xl bg-slate-900/60 border border-white/5 p-1" data-testid="invoice-filter">
          {[["all", "All"], ["paid", "Paid"], ["due", "Due"], ["cancelled", "Cancelled"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg text-xs transition ${filter === k ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
              data-testid={`if-${k}`}
            >
              {l} {filter !== k && filteredCounts[k] != null ? ` · ${filteredCounts[k]}` : ""}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-500" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") load(); }}
            placeholder="Search by number / customer…"
            className="input pl-8 w-72"
            data-testid="invoice-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center">
          <FileText size={32} className="text-blue-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-white font-semibold">No invoices yet</h3>
          <p className="text-slate-400 text-sm mt-1">Create your first invoice with the button above.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto" data-testid="admin-invoices-table">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                <th className="py-3 px-4 text-left">Number</th>
                <th className="text-left">Customer</th>
                <th className="text-left">Issued</th>
                <th className="text-right">Amount</th>
                <th className="text-left pl-4">Status</th>
                <th className="text-left pl-2">Transaction</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(inv => {
                const sym = CURRENCY_SYMBOL[inv.currency] || (inv.currency ? inv.currency + " " : "₹");
                return (
                  <tr key={inv.id} className="border-b border-slate-800" data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-2.5 px-4 text-cyan-300 font-mono">{inv.number}</td>
                    <td className="text-slate-300">
                      <div className="text-white">{inv.customer_name || "—"}</div>
                      <div className="text-slate-500 text-xs">{inv.customer_email}</div>
                    </td>
                    <td className="text-slate-400 text-xs">{fmtDate(inv.issued_at || inv.created_at)}</td>
                    <td className="text-right text-white font-mono font-bold">{sym}{Number(inv.amount).toLocaleString()}</td>
                    <td className="pl-4">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_CLS[inv.status] || "border-slate-700 text-slate-300"}`}>{(inv.status || "").toUpperCase()}</span>
                    </td>
                    <td className="pl-2 text-slate-400 text-xs">
                      {inv.transaction_id ? (
                        <div>
                          <div className="text-slate-300 font-mono truncate max-w-[140px]" title={inv.transaction_id}>{inv.transaction_id}</div>
                          {inv.bank_rrn && <div className="text-slate-500" title="Bank RRN">RRN: {inv.bank_rrn}</div>}
                          {inv.payment_method_detail && <div className="text-cyan-300/70">{inv.payment_method_detail}</div>}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="text-right pr-4">
                      <div className="inline-flex gap-1">
                        {inv.status === "due" && (
                          <button onClick={() => setMarking(inv)} title="Mark paid (record transaction)" className="text-emerald-300 hover:text-emerald-200 p-1.5" data-testid={`mark-paid-${inv.id}`}><Wallet size={16} /></button>
                        )}
                        {inv.status === "due" && (
                          <button onClick={() => generateLink(inv)} title={inv.payment_link ? "Regenerate Razorpay link" : "Generate Razorpay link"} className="text-blue-300 hover:text-blue-200 p-1.5" data-testid={`link-${inv.id}`}><LinkIcon size={16} /></button>
                        )}
                        {inv.payment_link && (
                          <button onClick={() => copyLink(inv)} title="Copy Razorpay payment link" className="text-cyan-300 hover:text-cyan-200 p-1.5" data-testid={`copy-link-${inv.id}`}><Copy size={16} /></button>
                        )}
                        <button onClick={() => setEditingDates(inv)} title="Edit issue / paid dates" className="text-amber-300 hover:text-amber-200 p-1.5" data-testid={`edit-dates-${inv.id}`}><CalendarClock size={16} /></button>
                        <button onClick={() => sendInvoice(inv)} title="Send / re-send by email" className="text-slate-300 hover:text-white p-1.5" data-testid={`send-${inv.id}`}><Mail size={16} /></button>
                        <button onClick={() => downloadPdf(inv, "customer")} title="Download customer copy (clean — no IP/geo)" className="text-emerald-300 hover:text-emerald-200 p-1.5" data-testid={`pdf-customer-${inv.id}`}><UserRound size={16} /></button>
                        <button onClick={() => downloadPdf(inv)} title="Download admin copy (with fraud metadata)" className="text-slate-300 hover:text-white p-1.5" data-testid={`pdf-${inv.id}`}><FileDown size={16} /></button>
                        {inv.status === "due" && (
                          <button onClick={() => cancelInvoice(inv)} title="Cancel" className="text-rose-300 hover:text-rose-200 p-1.5" data-testid={`cancel-${inv.id}`}><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateInvoiceModal onClose={() => { setCreating(false); load(); }} onSaved={() => { setCreating(false); load(); }} />}
      {importing && <ImportCSVModal onClose={() => setImporting(false)} onSaved={() => { setImporting(false); load(); }} />}
      {marking && <MarkPaidModal inv={marking} onClose={() => setMarking(null)} onSaved={() => { setMarking(null); load(); }} />}
      {editingDates && <EditDatesModal inv={editingDates} onClose={() => setEditingDates(null)} onSaved={() => { setEditingDates(null); load(); }} />}
    </div>
  );
}

function toLocalDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDT(local) {
  if (!local) return null;
  const d = new Date(local);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function EditDatesModal({ inv, onClose, onSaved }) {
  const [form, setForm] = useState({
    issued_at: toLocalDT(inv.issued_at || inv.created_at),
    due_date: toLocalDT(inv.due_date),
    paid_at: toLocalDT(inv.paid_at),
    payment_method: inv.payment_method || "razorpay",
    transaction_id: inv.transaction_id || "",
    bank_rrn: inv.bank_rrn || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (form.issued_at)  payload.issued_at = fromLocalDT(form.issued_at);
      if (form.due_date)   payload.due_date  = fromLocalDT(form.due_date);
      if (form.paid_at)    payload.paid_at   = fromLocalDT(form.paid_at);
      if (form.payment_method) payload.payment_method = form.payment_method;
      if (form.transaction_id) payload.transaction_id = form.transaction_id;
      if (form.bank_rrn) payload.bank_rrn = form.bank_rrn;
      await api.patch(`/admin/invoices/${inv.id}`, payload);
      toast.success("Invoice updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="edit-dates-modal">
      <div className="card max-w-lg w-full p-6 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Edit dates · {inv.number}</h3>
          <button onClick={onClose}><X className="text-slate-400" size={18}/></button>
        </div>
        <div>
          <label className="label">Issued on</label>
          <input type="datetime-local" value={form.issued_at} onChange={e=>setForm({...form, issued_at: e.target.value})} className="input w-full" data-testid="ed-issued-at"/>
        </div>
        <div>
          <label className="label">Due by</label>
          <input type="datetime-local" value={form.due_date} onChange={e=>setForm({...form, due_date: e.target.value})} className="input w-full" data-testid="ed-due-date"/>
        </div>
        <div>
          <label className="label">Paid on (manual payment)</label>
          <input type="datetime-local" value={form.paid_at} onChange={e=>setForm({...form, paid_at: e.target.value})} className="input w-full" data-testid="ed-paid-at"/>
          <p className="text-xs text-slate-500 mt-1">Set this when you record a payment received outside Razorpay (cash, UPI, bank transfer, etc.).</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Payment method</label>
            <select value={form.payment_method} onChange={e=>setForm({...form, payment_method: e.target.value})} className="input w-full" data-testid="ed-method">
              {["razorpay","upi","bank_transfer","cash","cheque","card","netbanking","wallet","paytm","pos","yespay_biz","au_qr_merchant","bijlipay","mswipe_pbl","other"].map(m =>
                <option key={m} value={m}>{PM_LABEL(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Transaction ID</label>
            <input value={form.transaction_id} onChange={e=>setForm({...form, transaction_id: e.target.value})} className="input w-full" data-testid="ed-txn"/>
          </div>
        </div>
        <div>
          <label className="label">Bank RRN (optional)</label>
          <input value={form.bank_rrn} onChange={e=>setForm({...form, bank_rrn: e.target.value})} className="input w-full" data-testid="ed-rrn"/>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="ed-save-btn">
            {busy ? <Loader2 className="animate-spin" size={14}/> : null} Save
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div></ModalPortal>
  );
}

function CreateInvoiceModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_email: "", customer_name: "",
    items: [{ name: "", quantity: 1, price: 0 }],
    currency: "INR", tax_percent: 18, discount: 0,
    issued_at: "", due_date: "", notes: "",
    auto_send_email: true, create_payment_link: true,
    // Retro-paid block — only sent if `retro_paid` is true
    paid_at: "", payment_method: "bank_transfer",
    transaction_id: "", bank_rrn: "",
  });
  const [retroPaid, setRetroPaid] = useState(false);
  const [saving, setSaving] = useState(false);

  const sym = CURRENCY_SYMBOL[form.currency] || form.currency + " ";
  const subtotal = form.items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);
  const taxAmt = Math.round((subtotal - Number(form.discount || 0)) * Number(form.tax_percent || 0) / 100 * 100) / 100;
  const total = Math.max(0, subtotal - Number(form.discount || 0) + taxAmt);

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, price: 0 }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updItem = (i, key, val) => {
    const items = [...form.items]; items[i] = { ...items[i], [key]: val }; setForm({ ...form, items });
  };

  // Open the freshly created invoice's PDF in a new tab using the admin
  // auth token (the endpoint is JWT-protected, so we fetch as a blob first).
  const previewPdfInline = async () => {
    if (!created?.invoice_id) return;
    try {
      const r = await api.get(`/admin/invoices/${created.invoice_id}/pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch { toast.error("Failed to load PDF preview"); }
  };

  const save = async () => {
    if (!form.customer_email || !form.customer_name) { toast.error("Customer email + name required"); return; }
    if (form.items.some(it => !it.name || it.price < 0)) { toast.error("Fill every line item"); return; }
    if (retroPaid && !form.transaction_id) { toast.error("Transaction ID is required for retro-paid invoices"); return; }
    if (retroPaid && !form.paid_at) { toast.error("Payment date is required for retro-paid invoices"); return; }
    setSaving(true);
    try {
      // Razorpay payment link only works for INR per backend rule, and we
      // never mint a link for retro-paid invoices (no point — already paid).
      const create_payment_link = !retroPaid && form.create_payment_link && form.currency === "INR";
      // Strip retro fields if the admin didn't ask to mark it paid right now
      const payload = {
        ...form,
        create_payment_link,
        // Convert blank ISO strings to undefined so the backend defaults kick in
        issued_at: form.issued_at ? new Date(form.issued_at).toISOString() : undefined,
        due_date: form.due_date || undefined,
        paid_at: retroPaid && form.paid_at ? new Date(form.paid_at).toISOString() : undefined,
        payment_method: retroPaid ? form.payment_method : undefined,
        transaction_id: retroPaid ? form.transaction_id : undefined,
        bank_rrn: retroPaid ? (form.bank_rrn || undefined) : undefined,
      };
      const { data } = await api.post("/admin/invoices", payload);
      // ALWAYS show in-modal confirmation so the admin gets clear feedback.
      // Previously we auto-closed when create_payment_link was off — users
      // reported "nothing happened / screen went blank" because the modal
      // disappeared without obvious confirmation.
      setCreated({
        number: data?.number || "—",
        payment_link: data?.payment_link || null,
        retro: retroPaid,
        emailed: !!form.auto_send_email,
        invoice_id: data?.id || null,
      });
      if (retroPaid) {
        toast.success(`Invoice ${data?.number || ""} created as PAID · linked order auto-added`);
      } else if (create_payment_link && data?.payment_link) {
        toast.success("Invoice created — payment link ready below");
      } else {
        toast.success("Invoice created" + (form.auto_send_email ? " and emailed" : ""));
      }
    } catch (e) {
      // FastAPI validation errors come back as an array under detail;
      // coerce them to a readable string so toast.error never receives a
      // raw object/array (which previously rendered as "[object Object]").
      const det = e?.response?.data?.detail;
      let msg = "Failed to create invoice";
      if (typeof det === "string") msg = det;
      else if (Array.isArray(det)) msg = det.map(d => d?.msg || JSON.stringify(d)).join("; ");
      else if (det && typeof det === "object") msg = det.msg || JSON.stringify(det);
      else if (e?.message) msg = e.message;
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const [created, setCreated] = useState(null);

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="create-invoice-modal">
      <div className="card max-w-3xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={18} /></button>
        <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "Outfit" }}>New invoice</h3>
        <p className="text-slate-400 text-xs mt-1">Auto-creates the customer account if it doesn&apos;t exist yet.</p>

        {created && (
          <div className="mt-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2.5" data-testid="ci-payment-link-banner">
            <div className="text-emerald-300 text-sm font-semibold inline-flex items-center gap-2" data-testid="ci-created-headline">
              <CheckCircle2 size={16}/>
              Invoice <span className="font-mono">{created.number}</span> created
              {created.retro && <span className="text-emerald-200">· marked PAID</span>}
              {created.emailed && !created.retro && <span className="text-emerald-200">· emailed to customer</span>}
            </div>
            {created.payment_link ? (
              <>
                <div className="text-xs text-slate-300">Razorpay payment link ready — copy &amp; share with the customer:</div>
                <div className="flex gap-2 items-center">
                  <input readOnly value={created.payment_link} className="input flex-1 text-xs" data-testid="ci-payment-link-input"/>
                  <button onClick={() => { navigator.clipboard.writeText(created.payment_link); toast.success("Copied"); }}
                          className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid="ci-copy-link-btn">
                    <Copy size={13}/> Copy
                  </button>
                  <a href={created.payment_link} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-3 text-xs">Open</a>
                </div>
              </>
            ) : (
              <div className="text-xs text-slate-300">
                {created.retro
                  ? "A matching PAID order has been auto-added to the Order List for delivery tracking."
                  : "The invoice is in the list below. You can email it, generate a Razorpay link, or mark it paid from the actions column."}
              </div>
            )}
            <div className="flex gap-2 pt-1 flex-wrap items-center">
              <button onClick={onSaved} className="btn-primary !py-1.5 !px-4 text-xs" data-testid="ci-done-btn">Done</button>
              {created.invoice_id && (
                <button
                  type="button"
                  onClick={previewPdfInline}
                  className="btn-ghost !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                  data-testid="ci-preview-pdf-btn"
                >
                  <ExternalLink size={12}/> Preview PDF
                </button>
              )}
              <button
                onClick={() => {
                  // Reset for "Create another" so admin can quickly add several invoices
                  setCreated(null);
                  setRetroPaid(false);
                  setForm({
                    customer_email: "", customer_name: "",
                    items: [{ name: "", quantity: 1, price: 0 }],
                    currency: "INR", tax_percent: 18, discount: 0,
                    issued_at: "", due_date: "", notes: "",
                    auto_send_email: true, create_payment_link: true,
                    paid_at: "", payment_method: "bank_transfer",
                    transaction_id: "", bank_rrn: "",
                  });
                }}
                className="btn-ghost !py-1.5 !px-4 text-xs"
                data-testid="ci-create-another-btn"
              >
                Create another
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div><label className="label">Customer name *</label><input className="input" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} data-testid="ci-customer-name" /></div>
          <div><label className="label">Customer email *</label><input type="email" className="input" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} data-testid="ci-customer-email" /></div>
          <div><label className="label">Currency</label>
            <select className="input" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} data-testid="ci-currency">
              {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{CURRENCY_SYMBOL[c]} {c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Issue date</label>
              <input type="datetime-local" className="input" value={form.issued_at} onChange={e => setForm({ ...form, issued_at: e.target.value })} data-testid="ci-issued-at"/>
            </div>
            <div>
              <label className="label">Due date</label>
              <input type="date" className="input" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="ci-due-date"/>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="label !mb-0">Line items</label>
            <button onClick={addItem} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid="ci-add-item"><Plus size={12} /> Add item</button>
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`ci-item-${i}`}>
                <input placeholder="Description" className="input col-span-6" value={it.name} onChange={e => updItem(i, "name", e.target.value)} />
                <input type="number" min="1" placeholder="Qty" className="input col-span-2 text-right" value={it.quantity} onChange={e => updItem(i, "quantity", Number(e.target.value))} />
                <input type="number" min="0" step="0.01" placeholder="Price" className="input col-span-3 text-right" value={it.price} onChange={e => updItem(i, "price", Number(e.target.value))} />
                <button onClick={() => rmItem(i)} disabled={form.items.length === 1} className="col-span-1 text-rose-300 hover:text-rose-200 disabled:opacity-30"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div><label className="label">Tax %</label><input type="number" min="0" max="100" step="0.01" className="input" value={form.tax_percent} onChange={e => setForm({ ...form, tax_percent: Number(e.target.value) })} /></div>
          <div><label className="label">Discount ({sym})</label><input type="number" min="0" step="0.01" className="input" value={form.discount} onChange={e => setForm({ ...form, discount: Number(e.target.value) })} /></div>
          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Total</div>
            <div className="text-2xl font-extrabold text-white" style={{ fontFamily: "Outfit" }} data-testid="ci-total">{sym}{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            <div className="text-[10px] text-slate-500">Subtotal {sym}{subtotal.toFixed(2)} · Tax {sym}{taxAmt.toFixed(2)}</div>
          </div>
        </div>

        <div className="mt-3"><label className="label">Notes (optional)</label><textarea rows="2" className="input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>

        {/* Retroactive payment recording block */}
        <div className="mt-4 border border-amber-500/30 bg-amber-500/5 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm text-amber-200 font-semibold cursor-pointer">
            <input type="checkbox" checked={retroPaid} onChange={e => setRetroPaid(e.target.checked)} data-testid="ci-retro-paid"/>
            🧾 Record as already paid (retroactive entry)
          </label>
          <p className="text-xs text-amber-300/70 mt-1">When enabled, the invoice will be created with status=paid and a matching <strong>paid order</strong> will be auto-added to the Order List so you can drive its delivery + completion workflow.</p>
          {retroPaid && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label">Payment date *</label>
                <input type="datetime-local" className="input" value={form.paid_at} onChange={e => setForm({ ...form, paid_at: e.target.value })} data-testid="ci-paid-at"/>
              </div>
              <div>
                <label className="label">Payment method *</label>
                <select className="input" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} data-testid="ci-payment-method">
                  {["bank_transfer","upi","cash","cheque","card","netbanking","wallet","razorpay","paytm","pos","yespay_biz","au_qr_merchant","bijlipay","mswipe_pbl","other"].map(m =>
                    <option key={m} value={m}>{PM_LABEL(m)}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Transaction ID *</label>
                <input className="input" value={form.transaction_id} onChange={e => setForm({ ...form, transaction_id: e.target.value })} data-testid="ci-transaction-id" placeholder="e.g. UTR-2026...0001234"/>
              </div>
              <div>
                <label className="label">Bank RRN (optional)</label>
                <input className="input" value={form.bank_rrn} onChange={e => setForm({ ...form, bank_rrn: e.target.value })} data-testid="ci-bank-rrn"/>
              </div>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-slate-300">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.auto_send_email} onChange={e => setForm({ ...form, auto_send_email: e.target.checked })} data-testid="ci-send-email" /> Email PDF to customer now</label>
          <label className={`flex items-center gap-2 ${(form.currency !== "INR" || retroPaid) ? "opacity-50" : ""}`}>
            <input type="checkbox" checked={!retroPaid && form.create_payment_link && form.currency === "INR"} disabled={form.currency !== "INR" || retroPaid} onChange={e => setForm({ ...form, create_payment_link: e.target.checked })} data-testid="ci-create-link" />
            Generate Razorpay payment link {form.currency !== "INR" && <span className="text-[10px] text-slate-500">(INR only)</span>}{retroPaid && <span className="text-[10px] text-slate-500">(skipped — already paid)</span>}
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="ci-cancel-btn">{created ? "Close" : "Cancel"}</button>
          {!created && (
            <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="ci-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {retroPaid ? "Create as paid" : "Create invoice"}
            </button>
          )}
        </div>
      </div>
    </div></ModalPortal>
  );
}

function MarkPaidModal({ inv, onClose, onSaved }) {
  const [form, setForm] = useState({
    transaction_id: "", bank_rrn: "",
    payment_method: "bank_transfer", note: "",
    send_confirmation_email: true,
  });
  const [saving, setSaving] = useState(false);
  const sym = CURRENCY_SYMBOL[inv.currency] || (inv.currency ? inv.currency + " " : "₹");

  const save = async () => {
    if (!form.transaction_id.trim()) { toast.error("Transaction ID is required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/invoices/${inv.id}/mark-paid`, form);
      toast.success("Payment recorded");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="mark-paid-modal">
      <div className="card max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={18} /></button>
        <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "Outfit" }}>Record manual payment</h3>
        <p className="text-slate-400 text-xs mt-1">Invoice {inv.number} · <span className="text-white">{sym}{Number(inv.amount).toLocaleString()}</span></p>

        <div className="space-y-3 mt-4">
          <div>
            <label className="label">Transaction ID *</label>
            <input className="input" value={form.transaction_id} onChange={e => setForm({ ...form, transaction_id: e.target.value })} placeholder="e.g. TXN-9876543 / UTR" data-testid="mp-tx-id" />
          </div>
          <div>
            <label className="label">Bank RRN (optional)</label>
            <input className="input" value={form.bank_rrn} onChange={e => setForm({ ...form, bank_rrn: e.target.value })} placeholder="12-digit Bank RRN" data-testid="mp-rrn" />
          </div>
          <div>
            <label className="label">Payment method *</label>
            <select className="input" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} data-testid="mp-method">
              {["upi", "netbanking", "card", "bank_transfer", "cash", "cheque", "wallet", "razorpay", "paytm", "pos", "yespay_biz", "au_qr_merchant", "bijlipay", "mswipe_pbl", "other"].map(m =>
                <option key={m} value={m}>{PM_LABEL(m)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Note (optional)</label>
            <textarea rows="2" className="input" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Anything you want recorded internally" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={form.send_confirmation_email} onChange={e => setForm({ ...form, send_confirmation_email: e.target.checked })} data-testid="mp-send-email" />
            Email payment confirmation + PDF to customer
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="mp-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Record payment
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}


// ============================================================================
// IMPORT CSV MODAL — bulk-create historical invoices (one row per invoice).
// Supports dry-run preview + result table with per-row errors.
// ============================================================================
function ImportCSVModal({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("optivize_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/import-csv/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "invoices-import-template.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Template download failed"); }
  };

  const submit = async () => {
    if (!file) { toast.error("Pick a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("optivize_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/import-csv?dry_run=${dryRun}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setResult(data);
      if (dryRun) {
        toast.success(`Dry-run: ${data.created_count} ok · ${data.error_count} errors`);
      } else {
        toast.success(`Imported ${data.created_count} invoices · ${data.error_count} errors`);
      }
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="import-csv-modal">
      <div className="card max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText size={18} className="text-blue-400"/> Import historical invoices from CSV
            </h3>
            <p className="text-slate-400 text-xs mt-1">One row per invoice. Rows with <code>paid_at</code> set are imported as PAID and a matching order is auto-spawned.</p>
          </div>
          <button onClick={onClose}><X className="text-slate-400" size={18}/></button>
        </div>

        <div className="mt-4 p-3 rounded-lg bg-slate-900/40 border border-slate-700 text-xs text-slate-300 space-y-1">
          <div><strong className="text-white">Required columns:</strong> <code>customer_email, customer_name, item_name, price</code></div>
          <div><strong className="text-white">Optional:</strong> <code>quantity, currency, tax_percent, discount, notes, issued_at, due_date, paid_at, payment_method, transaction_id, bank_rrn</code></div>
          <div className="text-amber-300/80">⚠ Rows with <code>paid_at</code> must include <code>transaction_id</code>.</div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-ghost !py-2 !px-3 text-xs inline-flex items-center gap-1" data-testid="csv-template-btn">
            <FileDown size={13}/> Download template
          </button>
        </div>

        <div className="mt-4">
          <label className="label">CSV file</label>
          <input type="file" accept=".csv,text/csv" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); }}
                 className="text-sm text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer w-full"
                 data-testid="csv-file-input"/>
          {file && <div className="text-[11px] text-slate-400 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-300 mt-3">
          <input type="checkbox" checked={dryRun} onChange={e => { setDryRun(e.target.checked); setResult(null); }} data-testid="csv-dry-run"/>
          Dry-run (validate without persisting) — recommended for the first attempt
        </label>

        {result && (
          <div className="mt-4 border-t border-slate-800 pt-3 space-y-3" data-testid="csv-result">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">✓ {result.created_count} {result.dry_run ? "valid" : "imported"}</span>
              <span className="px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200">✕ {result.error_count} errors</span>
              <span className="text-slate-500 text-xs">{result.dry_run ? "Nothing persisted yet. Uncheck dry-run and re-submit to actually import." : "Imported to the database."}</span>
            </div>

            {result.errors && result.errors.length > 0 && (
              <details open className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-3">
                <summary className="cursor-pointer text-rose-300 text-sm font-semibold">{result.errors.length} error rows</summary>
                <table className="w-full text-xs mt-2">
                  <thead className="text-rose-300/70"><tr><th className="text-left pr-3">Row</th><th className="text-left">Reason</th></tr></thead>
                  <tbody>
                    {result.errors.slice(0, 50).map((e, i) => (
                      <tr key={i} className="border-t border-rose-500/10" data-testid={`csv-error-${i}`}>
                        <td className="pr-3 py-1 font-mono text-rose-300">#{e.row}</td>
                        <td className="text-rose-200">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {result.created && result.created.length > 0 && (
              <details className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                <summary className="cursor-pointer text-emerald-300 text-sm font-semibold">{result.created.length} {result.dry_run ? "valid rows" : "created invoices"}</summary>
                <table className="w-full text-xs mt-2">
                  <thead className="text-emerald-300/70"><tr><th className="text-left pr-3">Row</th><th className="text-left pr-3">Customer</th><th className="text-left pr-3">Invoice</th><th className="text-left pr-3">Order</th><th className="text-right">Total</th></tr></thead>
                  <tbody>
                    {result.created.slice(0, 50).map((c, i) => (
                      <tr key={i} className="border-t border-emerald-500/10">
                        <td className="pr-3 py-1 font-mono">#{c.row}</td>
                        <td className="pr-3 text-slate-300">{c.customer_email || c.email}</td>
                        <td className="pr-3 font-mono text-emerald-200">{c.invoice_number || "—"}</td>
                        <td className="pr-3 font-mono text-cyan-200">{c.order_id ? `${c.order_id.slice(0,8)}…` : (c.will_be_paid ? "(will create)" : "—")}</td>
                        <td className="text-right text-white">{c.total ? `₹${Number(c.total).toLocaleString()}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-3">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4">Close</button>
          {result && !result.dry_run && result.created_count > 0 && (
            <button onClick={onSaved} className="btn-primary !py-2 !px-4" data-testid="csv-done-btn">Done — refresh list</button>
          )}
          <button onClick={submit} disabled={busy || !file} className="btn-primary inline-flex items-center gap-2" data-testid="csv-submit-btn">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>}
            {dryRun ? "Validate" : "Import"}
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}

